import { memo, useContext, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, message, Modal, Tooltip, Input } from 'antd';
import { DateTime } from 'luxon';
import localforage from 'localforage';

import { setOrder } from '../../store/actions/set/order';
import { getSavedOrders } from '../../store/actions/get/fetchSavedOrders';
import { saveForLater } from '../../store/actions/set/saveForLater';
import { setSaveForLaterOffline } from '../../store/actions/set/saveForLaterOffline';
import { setCustomersView } from '../../store/actions/set/customersView';
import { setPaymentView } from '../../store/actions/set/paymentView';
import { saveOpenOrder } from '../../store/actions/set/openOrder';
import { setCustomer } from '../../store/actions/set/customer';
import { setOilFloors } from '../../store/actions/set/oilFloors';
import { getCustomersPoints } from '../../store/actions/get/customersPoints';
import { setDisableCustomer } from '../../store/actions/set/disableCustomer';
import { setDiscardProductClicked } from '../../store/actions/set/discardProductClick';

import ErrorBoundary from '../ErrorBoundary';
import OrdersDialpad from '../OrdersDialpad';
import JobCardModal from '../JobCardModal';

import { DRIVETHRU, OIL_REST_HOTEL } from '../../constants';
import {
  generateId,
  calculateOrder,
  getDataFromIndexedDB,
  getDiscoundProduct,
  setDataInIndexedDB,
} from '../../utils';

import './ordersactions.scss';
import { setSyncingInLiveMode } from '../../store/actions/set/syncingInLiveMode';
import { SocketContext } from '../../Contexts/SocketContext';
import { validateOrder } from '../../store/actions/set/validateOrder';
import { getCustomerPreviousOrders } from '../../store/actions/get/customerPreviousOrders';
import { updateCustomerPoints } from '../../store/actions/set/updateCustomerPoints';
import { getTotalEarnedPoints, getTotalRedeemedPoints } from '../OrdersQueue';
import { getCustomers } from '../../store/actions/get/customers';
import { reduceProductQty } from '../../store/actions/set/inventory';
import { setViewAllOrders } from '../../store/actions/set/viewAllOrders';
import { setPaymentList } from '../../store/actions/set/paymentList';
import { setQuickPaymentStatus } from '../../store/actions/set/quickPaymentStatus';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Lottie from 'react-lottie';
import CircleCheckFast from '../../assets/CirlceCheckFast.json';
import Trash from '../../assets/Trash.json';
import { saveForSyncing } from '../../store/actions/set/saveForSyncing';
import { setSaveForLaterSyncing } from '../../store/actions/set/saveForLaterSyncing';
import { useOnline } from 'rooks';

const getTotal = (prev, cur) => prev + parseFloat(cur.lst_price) * cur.quantity;

export const generateOrder = (
  order,
  configInfo,
  creation_date,
  customer,
  tableView
) => {
  const getPartnerId = () => {
    if (order.partner_id && customer?.id && order.partner_id !== customer?.id) {
      return customer.id;
    }

    return order.partner_id || customer?.id || false;
  };

  return {
    name: order.name || generateId(),
    partner_id: getPartnerId(),
    lines: order.lines?.map((oL) => [
      0, // Ignore it: Required by odoo
      0, // Ignore it: Required by odoo
      {
        discount: 0,
        pack_lot_ids: [], // Required By Odoo
        id: oL.id * Math.floor(Math.random(Math.random() + 5) * 25),
        price_subtotal: Number(oL.lst_price) * Number(oL.quantity),
        price_subtotal_incl: Number(oL.lst_price) * Number(oL.quantity),
        product_id: oL.id,
        qty: oL.quantity,
        price_unit: oL.lst_price,
        react_instructions_list:
          oL.react_instructions_list?.map((l) => l.id)?.toString() || false,
        tax_ids: [[6, false, []]], // Second Array -> First Index: taxId(product), Second Index: true(include), Third Index: Children Tax info (Format same as second array),
        note: oL.note || '',
        tax_value: oL?.tax_value,
        custom_discount: oL?.custom_discount || false,
        custom_discount_type: oL?.custom_discount_type || false,
        custom_discount_value: oL?.custom_discount_value || false,
        is_redeemed: oL?.is_redeemed || false,
      },
    ]),
    global_discount_type: order?.global_discount_type || false,
    global_discount_value: order?.global_discount_value || false,
    global_tax_value: order?.global_tax_value || false,
    pos_session_id: configInfo?.session_id,
    pricelist_id: configInfo?.pricelist_id,
    sequence_number: Math.floor(Math.random(Math.random() + 1) + 5 * 23),
    server_id: false, // Ignore it: Required by odoo
    fiscal_position_id: false, // Ignore it: Required by odoo
    creation_date,
    statement_ids: [], // Ignore it: Required by odoo
    uid: order.name || generateId(),
    amount_paid: 0, // Ignore it: Required by odoo
    amount_return: 0, // Ignore it: Required by odoo
    amount_tax: Number(order.amount_tax) || 0,
    amount_total: Number(order.lines.reduce(getTotal, 0).toFixed(2)),
    user_id: configInfo?.current_user_id,
    state: 'draft',
    order_status: 'open',
    is_available: true,
    order_type:
      order.orderType ||
      order.order_type ||
      (configInfo.order_types_list?.length &&
        configInfo.order_types_list[0]?.id) ||
      false,
    employee_id: false, // Ignore it: Required by odoo
    customer_count: 1, // Ignore it: Required by odoo
    select_employees: [], // Ignore it: Required by odoo
    employees: [], // Ignore it: Required by odoo
    order_prefix: configInfo.order_prefix,
    table: tableView?.table_name,
    table_id: tableView?.table_id,
    floor: tableView?.floor_name,
    floor_id: tableView?.floor_id,
    car_id: customer?.car_id,
    current_reading: order.current_reading,
    car_reading_per_day: order.car_reading_per_day,
    km_for_next_service: order.km_for_next_service,
    reading_on_next_service: order.reading_on_next_service,
    date_of_next_service: order.date_of_next_service,
    worker_id: order.workerId || false,
    device_id: order?.device_id || false,
    unique_order_key: order.unique_order_key || order.name || generateId(),
    order_time: order.order_time || configInfo.default_order_timer || false,
  };
};

const Index = memo(() => {
  const online = useOnline();
  const { socket } = useContext(SocketContext);
  const dispatch = useDispatch();
  const order = useSelector((state) => state.order?.order);
  // const products = useSelector((state) => state.products.products);
  const customer = useSelector((state) => state.customer.customer);
  const configInfo = useSelector((state) => state.configInfo.configInfo);
  const orderType = useSelector((state) => state.orderType.orderType);
  const user = useSelector((state) => state.login.user);
  const selectedPos = useSelector((state) => state.selectedPos.selectedPos);
  const oilFloors = useSelector((state) => state.oilFloors?.oilFloors);
  const floors = useSelector((state) => state.floors?.floors);
  const tableView = useSelector((state) => state.tableView?.tableView);
  const isCustomerDisabled = useSelector(
    (state) => state.disableCustomer.status
  );
  const salesmen = useSelector((state) => state.salesmen.salesmen);
  const customers = useSelector((state) => state.customers.customers);
  const isSyncingInProgressInLiveMode = useSelector(
    (state) => state.isSyncingInProgressInLiveMode.status
  );

  const debounce = useSelector((state) => state.debounceTerm.debounceTerm);
  const cancelToken = useSelector((state) => state.cancelToken.cancelToken);
  const [display, setDisplay] = useState('');
  const [toggleValue, setToggleValue] = useState('percent');
  const [actionsModal, setActionsModal] = useState({
    status: false,
    action: '',
  });
  const [jobCardModal, setJobCardModal] = useState(false);
  const [isAuthenticated, setisAuthenticated] = useState(false);
  const [isSaveForLaterButtonDisabled, setIsSaveForLaterButtonDisabled] =
    useState(false);
  const [isOrderTimerModalOpen, setIsOrderTimerModalOpen] = useState(false);
  const [orderTimerValue, setOrderTimerValue] = useState(
    configInfo?.default_order_timer
  );

  const handleSaveForLaterOperations = async (isOkPressed = false) => {
    if (selectedPos?.pos_type === DRIVETHRU) {
      dispatch(setSyncingInLiveMode(true));
    }
    setIsSaveForLaterButtonDisabled(true);

    if (selectedPos?.pos_type === OIL_REST_HOTEL) {
      let selectedTableOrder;
      let selectedTableCustomer;
      (oilFloors?.length ? oilFloors : floors)?.find((f) => {
        if (f.id === tableView.floor_id) {
          selectedTableOrder = f.tables.find(
            (t) => t.id === tableView.table_id
          )?.order;
          const workerId = f.tables.find(
            (t) => t.id === tableView.table_id
          )?.workerId;
          selectedTableOrder = { ...selectedTableOrder, workerId };
          selectedTableCustomer = f.tables.find(
            (t) => t.id === tableView.table_id
          )?.customer;
        }
      });

      if (selectedTableOrder?.lines?.length) {
        const creationDate = DateTime.now().toUTC();
        const data = {
          data: generateOrder(
            { ...selectedTableOrder, orderType, device_id: user.device_id },
            configInfo,
            creationDate,
            selectedTableCustomer,
            tableView
          ),
          ...user,
        };

        if (window.navigator.onLine) {
          const response = await saveForLater(data);
          if (response?.status === 'success') {
            const newFloors = (oilFloors?.length ? oilFloors : floors)?.map(
              (f) => {
                if (f.id === tableView.floor_id) {
                  f.tables?.forEach((t) => {
                    if (t.id === tableView.table_id) {
                      if (t.order) {
                        t.order = null;
                      }
                      if (t.customer) {
                        t.customer = null;
                      }
                      if (t.workerId) {
                        t.workerId = null;
                      }
                    }
                  });
                }

                return f;
              }
            );

            dispatch(setOilFloors([...newFloors]));
            let requiredData = {
              ...user,
              sessionId: configInfo?.session_id,
              PosType: selectedPos?.pos_type,
            };
            if (selectedPos?.pos_type === OIL_REST_HOTEL) {
              requiredData = {
                ...requiredData,
                floor_id: tableView.floor_id,
                table_id: tableView.table_id,
              };
            }
            socket.emit('update_kds_screen', {
              ...user,
              sessionId: configInfo?.session_id,
              bump_screen_mode: configInfo?.bump_screen_mode,
              // configInfo,
            });
            // dispatch(getSavedOrders(requiredData));
            socket.emit('update_saved_orders_count', {
              ...requiredData,
            });

            if (
              configInfo?.allow_remote_access &&
              order?.order_status !== 'open'
            ) {
              socket.emit('update-live-screen', {
                order: 'saved',
                sessionId: configInfo?.session_id,
              });
            }
          }
        } else {
          const newFloors = (oilFloors?.length ? oilFloors : floors)?.map(
            (f) => {
              if (f.id === tableView.floor_id) {
                f.tables?.forEach((t) => {
                  if (t.id === tableView.table_id) {
                    if (t.id === tableView.table_id) {
                      if (t.orders?.length) {
                        t.orders = [
                          ...t.orders,
                          {
                            selectedTableOrder,
                            selectedTableCustomer,
                          },
                        ];
                      } else {
                        t.orders = [
                          {
                            selectedTableOrder,
                            selectedTableCustomer,
                          },
                        ];
                      }
                    }
                    if (t.order) {
                      t.order = null;
                    }
                    if (t.customer) {
                      t.customer = null;
                    }
                    if (t.workerId) {
                      t.workerId = null;
                    }
                  }
                });
              }

              return f;
            }
          );

          dispatch(setOilFloors([...newFloors]));
        }
      }
    } else if (order.lines?.length) {
      const creationDate = DateTime.now().toUTC();
      const data = {
        data: generateOrder(
          {
            ...order,
            orderType,
            order_time: isOkPressed
              ? orderTimerValue
              : configInfo?.default_order_timer,
            device_id: user.device_id,
          },
          configInfo,
          creationDate,
          customer
        ),
        ...user,
      };

      if (window.navigator.onLine) {
        //TODO:
        debounce.cancel();
        cancelToken?.cancel();
        const response = await saveForLater(data);
        if (response?.status === 'success') {
          dispatch(
            setOrder({
              name: generateId(),
              lines: [],
              salesmanId: undefined,
              orderType: undefined,
              payment: undefined,
              workerId: configInfo?.worker_fix ? order.workerId : undefined,
            })
          );
          // dispatch(
          //   getSavedOrders({
          //     ...user,
          //     sessionId: configInfo?.session_id,
          //     PosType: selectedPos?.pos_type,
          //   })
          // );
          socket.emit('update_saved_orders_count', {
            ...user,
            sessionId: configInfo?.session_id,
            PosType: selectedPos?.pos_type,
          });
          socket.emit('update_kds_screen', {
            ...user,
            sessionId: configInfo?.session_id,
            bump_screen_mode: configInfo?.bump_screen_mode,
            // configInfo,
          });
          if (
            configInfo?.allow_remote_access &&
            order?.order_status !== 'open'
          ) {
            socket.emit('update-live-screen', {
              order: 'saved',
              sessionId: configInfo?.session_id,
            });
          }
          debounce.cancel();
          dispatch(setSyncingInLiveMode(false));
          dispatch(setCustomer(undefined));
          dispatch(setDisableCustomer(false));
        } else {
          dispatch(setSyncingInLiveMode(false));
          dispatch(setCustomer(undefined));
          dispatch(setDisableCustomer(false));
        }
      } else {
        dispatch(
          setSaveForLaterOffline({
            ...order,
            partner_id: customer?.id,
            orderType,
          })
        );
        dispatch(
          setOrder({
            name: generateId(),
            lines: [],
            salesmanId: undefined,
            orderType: undefined,
            payment: undefined,
            workerId: configInfo?.worker_fix ? order.workerId : undefined,
          })
        );
        debounce.cancel();
        dispatch(setSyncingInLiveMode(false));
        dispatch(setCustomer(undefined));
        dispatch(setDisableCustomer(false));
      }
    }
    setIsSaveForLaterButtonDisabled(false);
  };

  const showModal = (e) => {
    setActionsModal({
      status: true,
      action: e.target.innerText === 'Update' ? 'Qty' : e.target.innerText,
    });
  };

  const handleOk = async (closeModal = true) => {
    const allProducts = await getDataFromIndexedDB('products', localforage);

    let discountProd = getDiscoundProduct(allProducts, configInfo);
    if (actionsModal.action === 'Qty' && Number(display)) {
      const creationDate = DateTime.now().toUTC();
      let updatedOrder = {
        ...order,
        lines: order.lines.map((line) =>
          line.isSelected
            ? {
              ...line,
              quantity: Number(display).toFixed(2),
            }
            : line
        ),
      };
      const data = {
        data: generateOrder(
          { ...updatedOrder, orderType, order_status: 'live' },
          configInfo,
          creationDate,
          customer
        ),
        ...user,
      };

      if (
        window.navigator.onLine &&
        configInfo?.live_order_sync &&
        selectedPos?.pos_type === DRIVETHRU
      ) {
        const orderName = await saveOpenOrder(data);
        if (orderName) {
          if (
            !(
              configInfo?.order_prefix &&
              typeof updatedOrder?.name !== 'boolean' &&
              updatedOrder?.name?.includes(configInfo.order_prefix)
            )
          ) {
            updatedOrder = { ...updatedOrder, name: orderName };
          }
        }
      }

      if (selectedPos?.pos_type === OIL_REST_HOTEL) {
        const newFloors = (oilFloors?.length ? oilFloors : floors)?.map((f) => {
          if (f.id === tableView.floor_id) {
            f.tables?.forEach((t) => {
              if (t.id === tableView.table_id && t.order?.lines.length) {
                t.order = {
                  ...t.order,
                  lines: t.order?.lines.map((line) =>
                    line.isSelected
                      ? {
                        ...line,
                        quantity: Number(display).toFixed(2),
                      }
                      : line
                  ),
                };
                if (t.order) {
                  t.order = {
                    ...t.order,
                    amount_tax: t.order.global_tax_value
                      ? Number(
                        Number(
                          Number(
                            t.order.lines
                              .filter((oL) =>
                                allProducts.find(
                                  (prod) =>
                                    prod.id === oL.id &&
                                    prod.id !== discountProd?.id &&
                                    prod.id !==
                                    configInfo?.fbr_service_fee_product
                                      ?.product_id
                                )
                              )
                              ?.reduce(getTotal, 0)
                              .toFixed(2)
                          )
                        ) * Number(Number(t.order.global_tax_value / 100))
                      ).toFixed(2)
                      : 0,
                    lines: calculateOrder(
                      allProducts,
                      t.order.lines,
                      configInfo?.global_discount_default_item?.product_id,
                      t.order.global_discount_type
                        ? t.order.global_discount_type
                        : false,
                      t.order.global_discount_value
                        ? t.order.global_discount_value
                        : false,
                      t.order.global_tax_value
                        ? t.order.global_tax_value
                        : false,
                      configInfo
                    ),
                  };
                }
              }
            });
          }

          return f;
        });

        dispatch(setOilFloors([...newFloors]));
      } else {
        const newUpdatedOrder = {
          ...updatedOrder,
          amount_tax: updatedOrder.global_tax_value
            ? Number(
              Number(
                Number(
                  updatedOrder.lines
                    .filter((oL) =>
                      allProducts.find(
                        (prod) =>
                          prod.id === oL.id &&
                          prod.id !== discountProd?.id &&
                          prod.id !==
                          configInfo?.fbr_service_fee_product?.product_id
                      )
                    )
                    ?.reduce(getTotal, 0)
                    .toFixed(2)
                )
              ) * Number(Number(updatedOrder.global_tax_value / 100))
            ).toFixed(2)
            : 0,
          lines: calculateOrder(
            allProducts,
            updatedOrder.lines,
            configInfo?.global_discount_default_item?.product_id,
            updatedOrder.global_discount_type
              ? updatedOrder.global_discount_type
              : false,
            updatedOrder.global_discount_value
              ? updatedOrder.global_discount_value
              : false,
            updatedOrder.global_tax_value
              ? updatedOrder.global_tax_value
              : false,
            configInfo
          ),
        };
        dispatch(
          setOrder({
            ...newUpdatedOrder,
          })
        );
      }
    }

    if (
      (actionsModal.action === 'Price' ||
        actionsModal.action === 'Price Update') &&
      display
    ) {
      let customDiscount;

      if (selectedPos?.pos_type === OIL_REST_HOTEL) {
        let selectedTableOrder;
        (oilFloors?.length ? oilFloors : floors)?.find((f) => {
          if (f.id === tableView.floor_id) {
            selectedTableOrder = f.tables.find(
              (t) => t.id === tableView.table_id
            )?.order;
          }
        });
        customDiscount = selectedTableOrder?.lines?.find(
          (line) => line.isSelected
        )?.custom_discount;
      } else {
        customDiscount = order.lines?.find(
          (line) => line.isSelected
        )?.custom_discount;
      }

      const discount = Number(customDiscount || 0) / 100;
      const newListPrice = Number(display) - Number(display) * discount;

      if (selectedPos?.pos_type === OIL_REST_HOTEL) {
        const newFloors = (oilFloors?.length ? oilFloors : floors)?.map((f) => {
          if (f.id === tableView.floor_id) {
            f.tables?.forEach((t) => {
              if (t.id === tableView.table_id && t.order?.lines.length) {
                t.order = {
                  ...t.order,
                  lines: t.order?.lines.map((line) =>
                    line.isSelected
                      ? {
                        ...line,
                        lst_price: line?.custom_discount
                          ? Number(newListPrice)
                          : Number(display),
                      }
                      : line
                  ),
                };
                if (t.order) {
                  t.order = {
                    ...t.order,
                    amount_tax: t.order.global_tax_value
                      ? Number(
                        Number(
                          Number(
                            t.order.lines
                              .filter((oL) =>
                                allProducts.find(
                                  (prod) =>
                                    prod.id === oL.id &&
                                    prod.id !== discountProd?.id &&
                                    prod.id !==
                                    configInfo?.fbr_service_fee_product
                                      ?.product_id
                                )
                              )
                              ?.reduce(getTotal, 0)
                              .toFixed(2)
                          )
                        ) * Number(Number(t.order.global_tax_value / 100))
                      ).toFixed(2)
                      : 0,
                    lines: calculateOrder(
                      allProducts,
                      t.order.lines,
                      configInfo?.global_discount_default_item?.product_id,
                      t.order.global_discount_type
                        ? t.order.global_discount_type
                        : false,
                      t.order.global_discount_value
                        ? t.order.global_discount_value
                        : false,
                      t.order.global_tax_value
                        ? t.order.global_tax_value
                        : false,
                      configInfo
                    ),
                  };
                }
              }
            });
          }

          return f;
        });

        dispatch(setOilFloors([...newFloors]));
      } else {
        const updatedOrder = {
          ...order,
          lines: order.lines.map((line) =>
            line.isSelected
              ? {
                ...line,
                lst_price: line?.custom_discount
                  ? Number(newListPrice)
                  : Number(display),
              }
              : line
          ),
        };
        const newUpdatedOrder = {
          ...updatedOrder,
          amount_tax: updatedOrder.global_tax_value
            ? Number(
              Number(
                Number(
                  updatedOrder.lines
                    .filter((oL) =>
                      allProducts.find(
                        (prod) =>
                          prod.id === oL.id &&
                          prod.id !== discountProd?.id &&
                          prod.id !==
                          configInfo?.fbr_service_fee_product?.product_id
                      )
                    )
                    ?.reduce(getTotal, 0)
                    .toFixed(2)
                )
              ) * Number(Number(updatedOrder.global_tax_value / 100))
            ).toFixed(2)
            : 0,
          lines: calculateOrder(
            allProducts,
            updatedOrder.lines,
            configInfo?.global_discount_default_item?.product_id,
            updatedOrder.global_discount_type
              ? updatedOrder.global_discount_type
              : false,
            updatedOrder.global_discount_value
              ? updatedOrder.global_discount_value
              : false,
            updatedOrder.global_tax_value
              ? updatedOrder.global_tax_value
              : false,
            configInfo
          ),
        };
        dispatch(
          setOrder({
            ...newUpdatedOrder,
          })
        );
      }
    }

    if (actionsModal.action === 'Disc.' && display) {
      if (toggleValue === 'percent' && (display > 100 || display < 0)) {
        message.error('Discount value must be between 0 and 100');
        return;
      }
      let discount;
      if (toggleValue === 'percent') {
        discount = Number(display) / 100;
      } else {
        discount = Number(display);
      }
      let listPrice;
      if (selectedPos?.pos_type === OIL_REST_HOTEL) {
        let selectedTableOrder;
        (oilFloors?.length ? oilFloors : floors)?.find((f) => {
          if (f.id === tableView.floor_id) {
            selectedTableOrder = f.tables.find(
              (t) => t.id === tableView.table_id
            )?.order;
          }
        });
        listPrice = allProducts.find(
          (prod) =>
            prod.id ===
            selectedTableOrder?.lines?.find((line) => line.isSelected)?.id
        ).lst_price;
        if (toggleValue === 'fixed' && (display > listPrice || display < 0)) {
          message.error("Discount value can't be less than original value");
          return;
        }
      } else {
        listPrice = allProducts.find(
          (prod) => prod.id === order.lines?.find((line) => line.isSelected)?.id
        )?.lst_price;
        if (toggleValue === 'fixed' && (display > listPrice || display < 0)) {
          message.error("Discount value can't be less than original value");
          return;
        }
      }
      let newListPrice;
      if (toggleValue === 'percent') {
        newListPrice = listPrice - listPrice * discount;
      } else {
        newListPrice = listPrice - discount;
      }

      if (selectedPos?.pos_type === OIL_REST_HOTEL) {
        const newFloors = (oilFloors?.length ? oilFloors : floors)?.map((f) => {
          if (f.id === tableView.floor_id) {
            f.tables?.forEach((t) => {
              if (t.id === tableView.table_id && t.order?.lines.length) {
                t.order = {
                  ...t.order,
                  lines: t.order?.lines?.map((line) =>
                    line?.isSelected
                      ? {
                        ...line,
                        lst_price: Number(newListPrice),
                        custom_discount_type: toggleValue,
                        custom_discount: Number(display).toFixed(2),
                        custom_discount_value:
                          toggleValue === 'percent'
                            ? Number(listPrice * discount).toFixed(2)
                            : Number(discount).toFixed(2),
                      }
                      : { ...line }
                  ),
                };
                if (t.order) {
                  t.order = {
                    ...t.order,
                    amount_tax: t.order.global_tax_value
                      ? Number(
                        Number(
                          Number(
                            t.order.lines
                              .filter((oL) =>
                                allProducts.find(
                                  (prod) =>
                                    prod.id === oL.id &&
                                    prod.id !== discountProd?.id &&
                                    prod.id !==
                                    configInfo?.fbr_service_fee_product
                                      ?.product_id
                                )
                              )
                              ?.reduce(getTotal, 0)
                              .toFixed(2)
                          )
                        ) * Number(Number(t.order.global_tax_value / 100))
                      ).toFixed(2)
                      : 0,
                    lines: calculateOrder(
                      allProducts,
                      t.order.lines,
                      configInfo?.global_discount_default_item?.product_id,
                      t.order.global_discount_type
                        ? t.order.global_discount_type
                        : false,
                      t.order.global_discount_value
                        ? t.order.global_discount_value
                        : false,
                      t.order.global_tax_value
                        ? t.order.global_tax_value
                        : false,
                      configInfo
                    ),
                  };
                }
              }
            });
          }

          return f;
        });

        dispatch(setOilFloors([...newFloors]));
      } else {
        const updatedOrder = {
          ...order,
          lines: order.lines.map((line) =>
            line.isSelected
              ? {
                ...line,
                lst_price: Number(display) > 100 ? 0 : Number(newListPrice),
                custom_discount_type: toggleValue,
                custom_discount: Number(display).toFixed(2),
                custom_discount_value:
                  toggleValue === 'percent'
                    ? Number(listPrice * discount).toFixed(2)
                    : Number(discount).toFixed(2),
              }
              : line
          ),
        };
        const newUpdatedOrder = {
          ...updatedOrder,
          amount_tax: updatedOrder.global_tax_value
            ? Number(
              Number(
                Number(
                  updatedOrder.lines
                    .filter((oL) =>
                      allProducts.find(
                        (prod) =>
                          prod.id === oL.id &&
                          prod.id !== discountProd?.id &&
                          prod.id !==
                          configInfo?.fbr_service_fee_product?.product_id
                      )
                    )
                    ?.reduce(getTotal, 0)
                    .toFixed(2)
                )
              ) * Number(Number(updatedOrder.global_tax_value / 100))
            ).toFixed(2)
            : 0,
          lines: calculateOrder(
            allProducts,
            updatedOrder.lines,
            configInfo?.global_discount_default_item?.product_id,
            updatedOrder.global_discount_type
              ? updatedOrder.global_discount_type
              : false,
            updatedOrder.global_discount_value
              ? updatedOrder.global_discount_value
              : false,
            updatedOrder.global_tax_value
              ? updatedOrder.global_tax_value
              : false,
            configInfo
          ),
        };
        dispatch(
          setOrder({
            ...newUpdatedOrder,
          })
        );
      }
    }

    setDisplay('');
    if (
      configInfo?.live_order_sync &&
      window.navigator.onLine &&
      selectedPos?.pos_type === DRIVETHRU
    ) {
      setTimeout(() => {
        dispatch(setDiscardProductClicked(true));
      }, 500);
    }
    if (closeModal) {
      setActionsModal({ status: false, action: '' });
    }
  };

  const handleSaveForLater = async () => {
    // const saveForLaterOperations = () => {
    if (selectedPos?.pos_type === DRIVETHRU && configInfo?.show_order_timer) {
      setIsOrderTimerModalOpen(true);
    } else {
      handleSaveForLaterOperations();
    }
    // };
    // if (isSyncingInProgressInLiveMode && configInfo?.live_order_sync) {
    //   setTimeout(() => {
    //     saveForLaterOperations();
    //   }, 1000);
    // } else {
    // saveForLaterOperations();
    // }
  };

  const getDisabledState = () => {
    if (selectedPos?.pos_type === OIL_REST_HOTEL) {
      let selectedTableOrder;
      (oilFloors?.length ? oilFloors : floors)?.find((f) => {
        if (f.id === tableView.floor_id) {
          selectedTableOrder = f.tables.find(
            (t) => t.id === tableView.table_id
          )?.order;
        }
      });

      return selectedTableOrder?.lines?.length ? false : true;
    }

    return order?.lines?.length ? false : true;
  };

  const getCustomerButtonText = () => {
    if (selectedPos?.pos_type === OIL_REST_HOTEL) {
      let selectedTableCustomer;
      (oilFloors?.length ? oilFloors : floors)?.find((f) => {
        if (f.id === tableView.floor_id) {
          selectedTableCustomer = f.tables.find(
            (t) => t.id === tableView.table_id
          )?.customer;
        }
      });

      return selectedTableCustomer ? (
        <Tooltip
          title={`${selectedTableCustomer.name} ${selectedTableCustomer.car?.vehicle_no
            ? `(${selectedTableCustomer.car?.vehicle_no})`
            : ''
            }`}
        >
          {selectedTableCustomer.name}
          <span className="points">
            {selectedTableCustomer.car?.vehicle_no
              ? `(${selectedTableCustomer.car?.vehicle_no})`
              : ''}
          </span>
        </Tooltip>
      ) : (
        'Job Card'
      );
    } else {
      return customer ? (
        <Tooltip
          title={`${customer.name} ${customer.points_earned ? `(${customer.points_earned})` : ''
            }`}
        >
          {customer.name}
          <span className="points">
            {customer.points_earned ? `(${customer.points_earned})` : ''}
          </span>
        </Tooltip>
      ) : (
        'Add Customer'
      );
    }
  };

  const showJobCardModal = () => {
    setJobCardModal(true);
  };

  const hideJobCardModal = () => {
    setJobCardModal(false);
  };

  const handleOrderTimeChange = async (isOkPressed) => {
    setIsOrderTimerModalOpen(!isOrderTimerModalOpen);
    handleSaveForLaterOperations(isOkPressed);
  };

  const getOrderLines = () => {
    if (selectedPos?.pos_type === OIL_REST_HOTEL) {
      let selectedTableOrder;
      (oilFloors?.length ? oilFloors : floors)?.find((f) => {
        if (f.id === tableView.floor_id) {
          selectedTableOrder = f.tables.find(
            (t) => t.id === tableView.table_id
          )?.order;
        }
      });

      return selectedTableOrder?.lines;
    }

    return order?.lines;
  };

  const getOrder = () => {
    if (selectedPos?.pos_type === OIL_REST_HOTEL) {
      let selectedTableOrder;
      (oilFloors?.length ? oilFloors : floors)?.find((f) => {
        if (f.id === tableView.floor_id) {
          selectedTableOrder = f.tables.find(
            (t) => t.id === tableView.table_id
          )?.order;
          const workerId = f.tables.find(
            (t) => t.id === tableView.table_id
          )?.workerId;
          const mechanic_name = salesmen?.find((s) => s.id === workerId)?.name;

          selectedTableOrder = {
            ...selectedTableOrder,
            workerId,
            mechanic_name,
          };
        }
      });

      return selectedTableOrder;
    }

    return order;
  };

  const getCustomer = () => {
    if (selectedPos?.pos_type === OIL_REST_HOTEL) {
      let selectedTableCustomer;
      (oilFloors?.length ? oilFloors : floors)?.find((f) => {
        if (f.id === tableView.floor_id) {
          selectedTableCustomer = f.tables.find(
            (t) => t.id === tableView.table_id
          )?.customer;
        }
      });

      return selectedTableCustomer;
    }

    return customer;
  };

  const handleNewOrderClick = () => {
    dispatch(setPaymentList([]));
    if (selectedPos?.pos_type === OIL_REST_HOTEL) {
      const newFloors = (oilFloors?.length ? oilFloors : floors)?.map((f) => {
        if (f.id === tableView.floor_id) {
          f.tables?.forEach((t) => {
            if (t.id === tableView.table_id) {
              if (t.order) {
                t.order = null;
              }
              if (t.customer) {
                t.customer = null;
              }
              if (t.workerId) {
                t.workerId = null;
              }
            }
          });
        }

        return f;
      });

      dispatch(setOilFloors([...newFloors]));
    } else {
      dispatch(
        setOrder({
          name: generateId(),
          lines: [],
          orderType: undefined,
          salesmanId: undefined,
          payment: undefined,
          workerId: configInfo?.worker_fix ? order.workerId : undefined,
        })
      );
      dispatch(setCustomer(false));
    }
    dispatch(setPaymentView(false));
    dispatch(setViewAllOrders(false));
  };

  const getEarnedPoints = () =>
    order.lines
      ?.filter((line) => line.reward_points)
      ?.reduce(getTotalEarnedPoints, 0)
      ?.toFixed(0);

  const getRedeemPoints = () =>
    order.lines
      ?.filter((line) => line.is_redeemed && line.reward_points)
      ?.reduce(getTotalRedeemedPoints, 0)
      ?.toFixed(0);

  const countDown = (status) => {
    let secondsToGo = 2;

    const icon = () => {
      switch (status) {
        case 'discarded':
          return (
            <Lottie
              options={{
                loop: false,
                autoplay: true,
                animationData: Trash,
              }}
            />
          );
        case 'paid':
        case 'saved':
          return (
            <Lottie
              options={{
                loop: false,
                autoplay: true,
                animationData: CircleCheckFast,
              }}
            />
          );
        default:
          break;
      }
    };

    const text = () => {
      switch (status) {
        case 'discarded':
          return 'Discarde';
        case 'paid':
          return 'Validated';
        case 'saved':
          return 'Saved';
        default:
          break;
      }
    };

    const modal = Modal.info({
      centered: true,
      content: (
        <div
          style={{
            height: '250px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          {icon()}
          <span style={{ fontSize: 'x-large', fontWeight: '500' }}>
            Order {text()},{' '}
            {/* {configInfo?.thankyou_message?.length
              ? configInfo?.thankyou_message
              : 'Thankyhou! for using YAZO!'} */}
          </span>
        </div>
      ),
      icon: false,
      okButtonProps: {
        hidden: true,
      },
    });

    const timer = setInterval(() => {
      secondsToGo -= 1;
      // modal.update({
      //   content: `This modal will be destroyed after ${secondsToGo} second.`,
      // });
    }, 500);

    setTimeout(() => {
      clearInterval(timer);
      modal.destroy();
    }, secondsToGo * 500);
  };


  const handleOrderValidation = async (data) => {
    const currentOrder = getOrder();
    dispatch(setQuickPaymentStatus(false));
    //TODO: Hello
    countDown('paid');
    const validateOrderResponse = await validateOrder({
      ...user,
      data,
      pos_type: selectedPos?.pos_type,
    });
    if (!validateOrderResponse) {
      dispatch(setQuickPaymentStatus(false))
      // setDisableValidateOrderButton(false);
      return;
    }

    if (
      validateOrderResponse &&
      typeof validateOrderResponse?.name === 'string' &&
      selectedPos?.pos_type !== OIL_REST_HOTEL
    ) {
      if (customer && selectedPos?.pos_type === DRIVETHRU) {
        let updatedPoints = await updateCustomerPoints({
          ...user,
          id: customer.id,
          pointsEarned:
            Number(customer.points_earned) -
            Number(getRedeemPoints() || 0) +
            Number(getEarnedPoints() || 0),
        });
        if (typeof updatedPoints === 'number') {
          const updatedCustomers = customers?.map((c) =>
            c.id === customer.id
              ? { ...c, points_earned: updatedPoints }
              : c
          );
          dispatch(getCustomers(undefined, [...updatedCustomers]));
        }
      }
    }
    if (configInfo?.allow_remote_access && order?.order_status !== 'open') {
      socket.emit('update-live-screen', {
        order: 'paid',
        sessionId: configInfo?.session_id,
      });
    }
    socket.emit('update_todays_orders_count', {
      ...user,
      sessionId: configInfo.session_id,
      posType: selectedPos?.pos_type,
    });
    socket.emit('update_kds_screen', {
      ...user,
      sessionId: configInfo?.session_id,
      bump_screen_mode: configInfo?.bump_screen_mode,
      // configInfo,
    });
    if (configInfo?.inventory_in_pos?.stock_location_id) {
      dispatch(
        reduceProductQty(
          getOrderLines()?.map((line) => ({
            id: line.id,
            quantity: line.quantity,
          }))
        )
      );
    }
    // dispatch(setQuickPaymentStatus(false));
    // //TODO: Hello
    // countDown('paid');
    // setDisableValidateOrderButton(false);
    if (isCustomerDisabled) {
      dispatch(setDisableCustomer(false));
    }
    // Update customer last order here
    if (customer && selectedPos?.pos_type === DRIVETHRU) {
      const totalCustomers = await getDataFromIndexedDB(
        'customers',
        localforage
      );
      const updatedTotalCustomers = totalCustomers?.map((c) =>
        c.id === customer.id
          ? {
            ...c,
            last_order_products: currentOrder.lines.map(
              (l) => l.display_name || l.name
            ),
          }
          : c
      );
      dispatch(getCustomers(undefined, [...updatedTotalCustomers]));
      await setDataInIndexedDB(
        'customers',
        updatedTotalCustomers,
        localforage
      );
    }
    handleNewOrderClick();
    if (!configInfo?.show_receipt_screen) {
      handleNewOrderClick();

      return;
    }
    // setReceiptView(true);
  }


  const handleSyncDone = async ({ user, data, pos_type }) => {
    console.log({ user, data, pos_type })
    const validateOrderResponse = await validateOrder({
      ...user,
      data,
      pos_type,
    });

    console.log("validateOrderResponse", { validateOrderResponse })

    if (!validateOrderResponse) {
      // setDisableValidateOrderButton(false);
      dispatch(setQuickPaymentStatus(false))
      return;
    }

    dispatch(
      setSaveForLaterSyncing({
        data,
        partner_id: customer?.id,
        orderType,
      }, true)
    );

    socket.emit('update_todays_orders_count', {
      ...user,
      sessionId: configInfo.session_id,
      posType: selectedPos?.pos_type,
    });
    socket.emit('update_kds_screen', {
      ...user,
      sessionId: configInfo?.session_id,
      bump_screen_mode: configInfo?.bump_screen_mode,
      // configInfo,
    });
  }


  const handleTapToPay = async ({ data, pos_type }) => {
    // const creationDate = DateTime.now().toU
    console.log({ data, order })
    if (order.lines?.length) {
      const creationDate = DateTime.now().toUTC();
      const saveForLaterData = {
        data: generateOrder(
          {
            ...order,
            orderType,
            order_time: false
              ? orderTimerValue
              : configInfo?.default_order_timer,
            device_id: user.device_id,
          },
          configInfo,
          creationDate,
          customer
        ),
        ...user,
      }
      const response = await saveForLater(saveForLaterData);
      console.log({ response })
      if (response?.status === 'success') {
        console.log({ response, name: response?.data?.result })

        data.name = response?.name;
        data.state = "draft";
        data.order_status = "open";


        console.log({ data })
        if (!order?.state) {
          socket.emit('tap-to-pay-initiated', {
            sessionId: configInfo?.session_id,
          });
        }
        let dataReq = JSON.stringify({
          Request: {
            Merchant: '00',
            TxnType: 'P',
            AmtPurchase: Number(
              Number(data?.amount_total).toFixed(2).toString().replace('.', '')
            ),
            TxnRef: response?.name?.split('-').join('').padStart(16, '0'),
            // TxnRef: "123456789121213131",
            CurrencyCode: 'AUD',
            CutReceipt: '0',
            ReceiptAutoPrint: '0',
            Application: '00',
            PurchaseAnalysisData: {
              OPR: '00766|test',
              AMT: '0042000',
              PCM: '0000',
            },
            Basket: {
              id: 't39kq18134553',
              amt: 2145,
              tax: 200,
              dis: 50,
              sur: 0,
              items: [
                {
                  id: 't39kq002',
                  sku: 'k24086723',
                  qty: 2,
                  amt: 2145,
                  tax: 200,
                  dis: 50,
                  name: 'XData USB Drive',
                },
              ],
            },
          },
        });

        let linklySecret = JSON.parse(
          localStorage.getItem('persistLogin')
        )?.linklySecret;
        if (!linklySecret) {
          message.error('No paired device found!', 2);
          dispatch(setQuickPaymentStatus(false))
          // setDisableValidateOrderButton(false);
          return;
        }

        const getAuth = await axios({
          method: 'post',
          maxBodyLength: Infinity,
          url: `${process.env.REACT_APP_LINKLY_BASE_URL}/tokens/cloudpos`,
          headers: {
            'Content-Type': 'application/json',
          },
          data: JSON.stringify({
            secret: linklySecret,
            posName: 'Yazo POS',
            posVersion: '1.0.0.0',
            posId: '41bb41e4-58a1-4e42-a669-1819739163da',
            posVendorId: '44be8cfd-e7dd-4343-b228-b25b13acd4d1',
          }),
        });

        if (!getAuth?.data.token) {
          let data = JSON.parse(localStorage.getItem('persistLogin'));
          delete data.linklySecret;
          localStorage.setItem(
            'persistLogin',
            JSON.stringify({
              ...data,
            })
          );
          // await localforage.removeItem('linklySecret');
          message.error(
            'Could not reach paired POS Device. Please pair POS Device again!',
            2
          );
          dispatch(setQuickPaymentStatus(false))
          // setDisableValidateOrderButton(false);
          return;
        }

        let sessionId = uuidv4();

        let config = {
          method: 'POST',
          maxBodyLength: Infinity,
          url: `${process.env.REACT_APP_LINKLY_TRANSACTION_URL}/${sessionId}/transaction?async=false`,
          headers: {
            Authorization: `Bearer ${getAuth?.data.token}`,
            'Content-Type': 'application/json',
          },
          data: dataReq,
        };

        axios(config)
          .then(async (response) => {
            console.log({ response })
            if (response?.data?.response?.responseCode !== "00" && response?.data?.response?.responseCode !== "08") {
              message.error(`${response?.data?.response?.responseText}! Please try again`, 2)
              dispatch(setQuickPaymentStatus(false));
              // setDisableValidateOrderButton(false);
              return;
            }
            // dispatch(setQuickPaymentStatus(false))
            await handleOrderValidation(data)
          })
          .catch(async function (error) {
            let status = error?.response?.status;
            // setDisableValidateOrderButton(false);
            dispatch(setQuickPaymentStatus(false))
            if (status === 401) {
              let data = JSON.parse(localStorage.getItem('persistLogin'));
              delete data.linklySecret;
              localStorage.setItem(
                'persistLogin',
                JSON.stringify({
                  ...data,
                })
              );
              message.error(
                "Couldn't process payment. Re-Pair your POS Device!",
                2
              );
              return;
            }
            if (status === 400) {
              message.error("Couldn't process payment. Verify Request Data!", 2);
              return;
            }
            if (status === 408) {
              message.error('Request Timeout. Please Try again!', 2);
              let delay = 1;
              let retryCount = 0;
              while (true) {
                const getResponse = await axios({
                  method: 'GET',
                  maxBodyLength: Infinity,
                  url: `${process.env.REACT_APP_LINKLY_TRANSACTION_URL}/${sessionId}/transaction?async=false`,
                  headers: {
                    Authorization: `Bearer ${getAuth?.data.token}`,
                    'Content-Type': 'application/json',
                  },
                })
                if (getResponse?.status === 408) {
                  await new Promise(resolve => setTimeout(resolve, delay * 1000));
                  delay *= 2;
                } else if (getResponse.status === 404) {
                  // Transaction not successful, exit recovery mode
                  break;
                } else if (getResponse.status === 202) {
                  // Transaction in progress, keep retrying
                  delay = 1; // Reset delay
                  await new Promise(resolve => setTimeout(resolve, 1000));
                } else if (getResponse.status === 200) {
                  await handleOrderValidation(data);
                  return;
                }
              }
            }
          });
        dispatch(setCustomer(undefined));
        dispatch(setDisableCustomer(false));
      } else {
        // dispatch(setSyncingInLiveMode(false));
        dispatch(setCustomer(undefined));
        dispatch(setDisableCustomer(false));
      }
    }
    return;
  };
  const handleValidateOrderOperations = async (isOkPressed = false, isTapToPay = false) => {
    console.log({ configInfo })

    const orderPayment = Number(
      getOrderLines()?.reduce(getTotal, 0)?.toFixed(2)
    );
    // let currentPayment = 0;
    // let amountReturn = 0;
    // paymentList.forEach((list) => {
    //   currentPayment += Number(list.prices.amount);
    //   amountReturn += Number(list.prices.change);
    // });

    // if (currentPayment < orderPayment) {
    //   message.error('Order payment is not fulfilled!', 1);
    //   return;
    // }

    dispatch(setQuickPaymentStatus(true));

    if (selectedPos?.pos_type === OIL_REST_HOTEL) {
      const activeOrder = getOrder();
      const selectedCustomer = getCustomer();
      if (
        selectedCustomer &&
        (!activeOrder.car_reading_per_day ||
          !activeOrder.current_reading ||
          !activeOrder.date_of_next_service ||
          !activeOrder.km_for_next_service)
      ) {
        message.error('Please Add Car Readings!!!', 1);
        dispatch(setQuickPaymentStatus(false));
        // setSelectedCustomerCar({ ...selectedCustomer.car });
        // setIsCarModalVisible(true);
        return;
      }
    }

    const creationDate = DateTime.now().toUTC();
    const currentOrder = getOrder();
    console.log({ currentOrder })
    const data = {
      ...generateOrder(
        {
          ...currentOrder,
          orderType:
            currentOrder?.orderType || currentOrder?.order_type || orderType,
          order_time: isOkPressed
            ? orderTimerValue
            : configInfo?.default_order_timer,
        },
        configInfo,
        creationDate,
        getCustomer(),
        tableView
      ),
      points_balance: (customer?.points_earned
        ? Number(customer?.points_earned)
        : 0) +
        (getEarnedPoints(currentOrder)
          ? Number(getEarnedPoints(currentOrder))
          : 0) -
        (getRedeemPoints(currentOrder)
          ? Number(getRedeemPoints(currentOrder))
          : 0),
      state: 'paid',
      order_status: 'final',
      comments: null,
      amount_paid: orderPayment,
      amount_return: 0,
      to_invoice: false,
      statement_ids: [[
        0,
        0,
        {
          card_type: '', // Required By Odoo
          payment_status: '',
          ticket: '',
          transaction_id: '',
          amount: Number(orderPayment),
          name: DateTime.now().toFormat('yyyy-LL-dd HH:mm:ss'),
          payment_method_id: isTapToPay ? configInfo?.payment_method?.find((m) => m.name?.toLowerCase() === 'tap to pay')?.id : configInfo?.payment_method.find(
            (m) => m.name.toLowerCase() === 'bank'
          )?.id,
        },
      ]],
    };
    if (isTapToPay) {
      await handleTapToPay({
        user: { ...user },
        data: data,
        pos_type: selectedPos?.pos_type,
      });
      return;
    }
    // return;

    //TODO: Order Syncing Process should be here.


    const validateOrderResponse = await saveForSyncing({
      ...user,
      data: {
        ...data,
        statement_ids: []
      },
      pos_type: selectedPos?.pos_type,
    });


    if (!validateOrderResponse) {
      dispatch(setQuickPaymentStatus(false));
      return;
    }

    console.log({ validateOrderResponse })

    data.name = validateOrderResponse?.name;
    console.log({ data })

    dispatch(
      setSaveForLaterSyncing({
        data,
        partner_id: customer?.id,
        orderType,
      })
    );

    //TODO: Handle Validation and remove from syncorder
    handleSyncDone({ user: { ...user }, data, pos_type: selectedPos?.pos_type });

    dispatch(
      getCustomerPreviousOrders({
        ...user,
        id: null,
        pos_type: selectedPos?.pos_type,
        loadSameDay: true,
      })
    );

    if (
      validateOrderResponse &&
      typeof validateOrderResponse?.name === 'string' &&
      selectedPos?.pos_type !== OIL_REST_HOTEL
    ) {
      if (customer && selectedPos?.pos_type === DRIVETHRU) {
        let updatedPoints = await updateCustomerPoints({
          ...user,
          id: customer.id,
          pointsEarned:
            Number(customer.points_earned) -
            Number(getRedeemPoints() || 0) +
            Number(getEarnedPoints() || 0),
        });
        if (typeof updatedPoints === 'number') {
          const updatedCustomers = customers?.map((c) =>
            c.id === customer.id ? { ...c, points_earned: updatedPoints } : c
          );
          dispatch(getCustomers(undefined, [...updatedCustomers]));
        }
      }
    }
    // dispatch(
    //   getTodaysOrders({
    //     ...user,
    //     sessionId: configInfo.session_id,
    //     PosType: selectedPos?.pos_type,
    //   })
    // );
    if (configInfo?.allow_remote_access && order?.order_status !== 'open') {
      socket.emit('update-live-screen', {
        order: 'paid',
        sessionId: configInfo?.session_id,
      });
    }
    socket.emit('update_todays_orders_count', {
      ...user,
      sessionId: configInfo.session_id,
      posType: selectedPos?.pos_type,
    });
    socket.emit('update_kds_screen', {
      ...user,
      sessionId: configInfo?.session_id,
      bump_screen_mode: configInfo?.bump_screen_mode,
      // configInfo,
    });
    if (configInfo?.inventory_in_pos?.stock_location_id) {
      dispatch(
        reduceProductQty(
          getOrderLines()?.map((line) => ({
            id: line.id,
            quantity: line.quantity,
          }))
        )
      );
    }
    dispatch(setQuickPaymentStatus(false));
    //TODO: Working on payment success modal
    countDown('paid');
    if (isCustomerDisabled) {
      dispatch(setDisableCustomer(false));
    }
    // Update customer last order here
    if (customer && selectedPos?.pos_type === DRIVETHRU) {
      const totalCustomers = await getDataFromIndexedDB(
        'customers',
        localforage
      );
      let updatedTotalCustomers = totalCustomers?.map((c) =>
        c.id === customer.id
          ? {
            ...c,
            last_order_products: currentOrder.lines.map(
              (l) => l.display_name || l.name
            ),
          }
          : c
      );
      dispatch(getCustomers(undefined, [...updatedTotalCustomers]));
      await setDataInIndexedDB('customers', updatedTotalCustomers, localforage);
    }
    handleNewOrderClick();
    // if (!configInfo?.show_receipt_screen) {
    //   console.log("im here")
    //   handleNewOrderClick();
    //   return;
    // }
  };

  return (
    <ErrorBoundary>
      <div className="orders-actions">
        {/* <div
          className="actions-wrap"
          style={
            configInfo.enlarge_action_button
              ? {
                gridTemplateColumns: 'calc(50% - 0.2dvw) calc(50% + 0.2dvw)',
              }
              : {}
          }
        >
          <Button
            className={`add-customer ${configInfo.enlarge_action_button ? ' add-customer-large' : ''
              }`}
            disabled={isCustomerDisabled}
            onClick={() => {
              if (selectedPos?.pos_type === DRIVETHRU) {
                socket.emit('update_customer_points', {
                  ...user,
                  sessionId: configInfo?.session_id,
                });
                // dispatch(getCustomersPoints(user));
              }
              dispatch(setCustomersView(true));
              // if (selectedPos?.pos_type === OIL_REST_HOTEL) {
              //   showJobCardModal();
              // }
            }}
          >
            {getCustomerButtonText()}
          </Button>
          <div
            className={`${selectedPos?.pos_type === OIL_REST_HOTEL ? 'oil-actions' : ''
              }`}
          >
            <Button
              type="primary"
              onClick={showModal}
              disabled={getDisabledState()}
              className={`update-action-button${configInfo.enlarge_action_button
                ? ' price-update-color-ful'
                : ''
                }`}
            >
              {configInfo.enlarge_action_button ? 'Price Update' : 'Update'}
            </Button>
          </div>
        </div>
        <div
          className="payment-wrap"
          style={
            configInfo.enlarge_action_button
              ? {
                gridTemplateColumns: 'calc(50% - 0.2dvw) calc(50% - 0.3dvw)',
              }
              : {}
          }
        >
          <Button
            className={`${configInfo.enlarge_action_button ? 'payment-large' : 'payment'
              }`}
            type="primary"
            disabled={getDisabledState()}
            onClick={() => {
              let isWorkerSelected = false;
              if (selectedPos?.pos_type === OIL_REST_HOTEL) {
                (oilFloors?.length ? oilFloors : floors)?.map((f) => {
                  if (f.id === tableView.floor_id) {
                    f.tables?.forEach((t) => {
                      if (t.id === tableView.table_id) {
                        if (t.workerId) {
                          isWorkerSelected = true;
                        }
                      }
                    });
                  }
                });
              } else {
                if (order?.workerId) {
                  isWorkerSelected = true;
                }
              }
              isWorkerSelected
                ? dispatch(setPaymentView(true))
                : message.warning('Please select worker!', 1);
            }}
          >
            Payment
          </Button>
          <Button
            className={`save-for-later ${configInfo.enlarge_action_button ? ' save-for-later-large' : ''
              }`}
            disabled={getDisabledState() || isSaveForLaterButtonDisabled}
            onClick={() => {
              let isWorkerSelected = false;
              if (selectedPos?.pos_type === OIL_REST_HOTEL) {
                (oilFloors?.length ? oilFloors : floors)?.map((f) => {
                  if (f.id === tableView.floor_id) {
                    f.tables?.forEach((t) => {
                      if (t.id === tableView.table_id) {
                        if (t.workerId) {
                          isWorkerSelected = true;
                        }
                      }
                    });
                  }
                });
              } else {
                if (order?.workerId) {
                  isWorkerSelected = true;
                }
              }
              isWorkerSelected
                ? handleSaveForLater()
                : message.warning('Please select worker!', 1);
            }}
          >
            {configInfo.enlarge_action_button ? 'Save' : 'Save For Later'}
          </Button>
        </div> */}
        <div class="actions-container">
          <div class="add-customer-action"><Button
            className={`add-customer ${configInfo.enlarge_action_button ? ' add-customer-large' : ''
              }`}
            disabled={isCustomerDisabled}
            onClick={() => {
              if (selectedPos?.pos_type === DRIVETHRU) {
                socket.emit('update_customer_points', {
                  ...user,
                  sessionId: configInfo?.session_id,
                });
                // dispatch(getCustomersPoints(user));
              }
              dispatch(setCustomersView(true));
              // if (selectedPos?.pos_type === OIL_REST_HOTEL) {
              //   showJobCardModal();
              // }
            }}

          >
            {getCustomerButtonText()}
          </Button></div>
          <div class="quick-pay-action">
            <Button
              className={`${configInfo.enlarge_action_button ? 'payment-large' : 'payment'
                }`}
              type="primary"
              disabled={getDisabledState() || !online || !configInfo?.payment_method?.find((m) => m.name?.toLowerCase() === 'tap to pay')}
              onClick={() => {
                let isWorkerSelected = false;
                if (selectedPos?.pos_type === OIL_REST_HOTEL) {
                  (oilFloors?.length ? oilFloors : floors)?.map((f) => {
                    if (f.id === tableView.floor_id) {
                      f.tables?.forEach((t) => {
                        if (t.id === tableView.table_id) {
                          if (t.workerId) {
                            isWorkerSelected = true;
                          }
                        }
                      });
                    }
                  });
                } else {
                  if (order?.workerId) {
                    isWorkerSelected = true;
                  }
                }
                let linklySecret = JSON.parse(
                  localStorage.getItem('persistLogin')
                )?.linklySecret;
                if (!linklySecret) {
                  message.error('No paired device found!', 2);
                  // setDisableValidateOrderButton(false);
                  return;
                }
                isWorkerSelected
                  ? handleValidateOrderOperations(false, true)
                  : message.warning('Please select worker!', 1);
              }}
            >
              Tap to Pay
            </Button>
          </div>
          <div class="price-update-action"><Button
            type="primary"
            onClick={showModal}
            disabled={getDisabledState()}
            className={`update-action-button${configInfo.enlarge_action_button
              ? ' price-update-color-ful'
              : ''
              }`}
          >
            {configInfo.enlarge_action_button ? 'Price Update' : 'Update'}
          </Button></div>
          <div class="quick-pay-action"><Button
            className={`${configInfo.enlarge_action_button ? 'payment-large' : 'payment'
              }`}
            type="primary"
            disabled={getDisabledState() || !online}
            onClick={() => {
              let isWorkerSelected = false;
              if (selectedPos?.pos_type === OIL_REST_HOTEL) {
                (oilFloors?.length ? oilFloors : floors)?.map((f) => {
                  if (f.id === tableView.floor_id) {
                    f.tables?.forEach((t) => {
                      if (t.id === tableView.table_id) {
                        if (t.workerId) {
                          isWorkerSelected = true;
                        }
                      }
                    });
                  }
                });
              } else {
                if (order?.workerId) {
                  isWorkerSelected = true;
                }
              }
              isWorkerSelected
                ? handleValidateOrderOperations()
                : message.warning('Please select work, isTapToPay = falseer!', 1);
            }}
          >
            <img src='./lightning.png' height={20} width={15} alt='Quick Pay' style={{ marginRight: '0.25dvw' }} /> Quick Pay
          </Button></div>
          <div class="payment-action"><Button
            className={`${configInfo.enlarge_action_button ? 'payment-large' : 'payment'
              }`}
            type="primary"
            disabled={getDisabledState()}
            onClick={() => {
              let isWorkerSelected = false;
              if (selectedPos?.pos_type === OIL_REST_HOTEL) {
                (oilFloors?.length ? oilFloors : floors)?.map((f) => {
                  if (f.id === tableView.floor_id) {
                    f.tables?.forEach((t) => {
                      if (t.id === tableView.table_id) {
                        if (t.workerId) {
                          isWorkerSelected = true;
                        }
                      }
                    });
                  }
                });
              } else {
                if (order?.workerId) {
                  isWorkerSelected = true;
                }
              }
              isWorkerSelected
                ? dispatch(setPaymentView(true))
                : message.warning('Please select worker!', 1);
            }}
          >
            Payment
          </Button></div>

          <div class="save-action"><Button
            className={`save-for-later ${configInfo.enlarge_action_button ? ' save-for-later-large' : ''
              }`}
            disabled={getDisabledState() || isSaveForLaterButtonDisabled}
            onClick={() => {
              let isWorkerSelected = false;
              if (selectedPos?.pos_type === OIL_REST_HOTEL) {
                (oilFloors?.length ? oilFloors : floors)?.map((f) => {
                  if (f.id === tableView.floor_id) {
                    f.tables?.forEach((t) => {
                      if (t.id === tableView.table_id) {
                        if (t.workerId) {
                          isWorkerSelected = true;
                        }
                      }
                    });
                  }
                });
              } else {
                if (order?.workerId) {
                  isWorkerSelected = true;
                }
              }
              isWorkerSelected
                ? handleSaveForLater()
                : message.warning('Please select worker!', 1);
            }}
          >
            {configInfo.enlarge_action_button ? 'Save' : 'Save For Later'}
          </Button></div>
        </div>
      </div>
      <Modal
        visible={actionsModal.status}
        destroyOnClose={true}
        onCancel={() => {
          setisAuthenticated(false);
          setDisplay('');
          setActionsModal({ status: false, action: '' });
        }}
        maskClosable={false}
        className="orders-dialpad-modal"
        footer={null}
      >
        <OrdersDialpad
          action={actionsModal.action}
          display={display}
          setDisplay={setDisplay}
          handleOk={handleOk}
          setActionsModal={setActionsModal}
          actionsModal={actionsModal}
          discType={toggleValue}
          setDiscType={setToggleValue}
          isAuthenticated={isAuthenticated}
          setisAuthenticated={setisAuthenticated}
        />
      </Modal>
      <Modal
        visible={jobCardModal}
        onCancel={hideJobCardModal}
        className="job-card-modal"
        footer={
          <Button
            type="primary"
            size="large"
            onClick={() => dispatch(setCustomersView(true))}
          >
            View Customers Page
          </Button>
        }
      >
        <JobCardModal hideModalAction={hideJobCardModal} />
      </Modal>
      <Modal
        title="Please Set Order Time In Minutes"
        visible={isOrderTimerModalOpen}
        onCancel={() => handleOrderTimeChange()}
        onOk={() => handleOrderTimeChange(true)}
      >
        <Input
          type="number"
          size="large"
          placeholder="Please Add Order Time In Minutes"
          value={orderTimerValue}
          onChange={(e) => setOrderTimerValue(Number(e.target.value))}
          style={{ width: '100%' }}
        />
      </Modal>
    </ErrorBoundary>
  );
});

export default Index;
